import styles from "../../assets/scss/modules/PublicListingMap.module.scss";
import React, {Fragment, useEffect, useMemo, useRef, useState} from 'react';
import {CtrDomainsVf, CtrInscriptions} from "../../graphql/generated/graphql-main.tsx";
import {AdvancedMarker, InfoWindow, useMap} from "@vis.gl/react-google-maps";
import {Marker, MarkerClusterer, Renderer} from "@googlemaps/markerclusterer";
import useCtrInscription from "../../hooks/useCtrInscription.tsx";
import {findBy} from "../../utils/array-utils.ts";
import useCtrGridListings from "../../hooks/useCtrGridListings.ts";
import svgEvFavicon from "../../assets/images/ev-favicon.svg";
import svgMgFavicon from "../../assets/images/mcgill-logo.svg";
import imgPlaceholder from "../../assets/images/img-placeholder.jpg";
import Card from "@mui/material/Card";
import {CardActionArea, Chip, useMediaQuery, useTheme} from "@mui/material";
import {useWpContext} from "../context/WordPressProvider.tsx";
import {cfg} from "../../config.ts";
import clsx from "clsx";
import useCtrValeursFixes from "../../hooks/useCtrValeursFixes.ts";
import {empty} from "../../utils/utils.ts";
import useSiteConfig from "../../hooks/useSiteConfig.ts";
import useReactTexts from "../../hooks/useReactTexts.ts";

type Props = {
    listings: CtrInscriptions[];
    mapActivityFlag: number;
}

/**
 * Given an array of {@link CtrInscriptions} entries, dynamically generate all markers in memoized format
 * to ensure optimal performance when loading thousands of markers on the map.
 * Make each marker interactive with info window when clicking on it.
 * @param props
 * @constructor
 */
const MapViewMarkers = (props: Props) => {
    const xtheme = useTheme();
    const {others} = useWpContext<ZListings>();
    const {__} = useReactTexts();
    const {price, shortPrice, title, shortAddress} = useCtrInscription();
    const {photoByMls, centrisPhoto} = useCtrGridListings();
    const {fixedValue} = useCtrValeursFixes();
    const {$config} = useSiteConfig();

    const is_sm = useMediaQuery(xtheme.breakpoints.up("sm"));

    // map-related refs, hooks, and local state
    const map = useMap();
    const [markers, setMarkers] = useState<{ [mlsId: string]: Marker }>({});
    const [activeMlsId, setActiveMlsId] = useState<string | null>(null);
    const clusterer = useRef<MarkerClusterer | null>(null);

    // given marker object, update its reference or add new marker (critical to ensure proper clustering)
    const setMarkerRef = (marker: Marker | null, key: string) => {
        if (marker && markers[key]) return;
        if (!marker && !markers[key]) return;
        setMarkers(prevState => {
            if (marker) {
                return {...prevState, [key]: marker};
            } else {
                const newMarkers = {...prevState};
                delete newMarkers[key];
                return newMarkers;
            }
        });
    }

    // center map to always view all markers in same viewport
    const centerMap = () => {
        // when no markers, set default center point and zoom it appropriately
        if (empty(props.listings)) {
            map?.setCenter(cfg.react.maps_view.center);
            map?.setZoom(8);
            return;
        }

        // generate bounds from actual listings' markers
        let bounds = new google.maps.LatLngBounds();
        props.listings.forEach(it => {
            bounds.extend({lat: parseFloat(it.LATITUDE!), lng: parseFloat(it.LONGITUDE!)});
        });
        // use different padding x-axis vs y-axis due to rectangular ratio of the map
        map?.fitBounds(bounds, {
            left: is_sm ? 300 : 100,
            right: is_sm ? 300 : 100,
            top: is_sm ? 150 : 50,
            bottom: is_sm ? 150 : 50,
        }); // pure pixel count

        // note: not ideal to use ratio of height as padding because it's less controllable (hard pixels = better)
        // map?.fitBounds(bounds, 0.20 * computedMapHeight());
    }

    // Build custom cluster marker renderer (@googlemaps/markerclusterer)
    const clusterRenderer: Renderer = useMemo(() => {
        const _renderer: Renderer = {
            render: (cluster, stats, map1) => {
                const _node = document.createElement("div");
                _node.className = clsx(styles.cluster_marker, {
                    "--l1": cluster.count < 10,
                    "--l2": cluster.count >= 10 && cluster.count < 100,
                    "--l3": cluster.count >= 100
                });
                _node.innerHTML = `${cluster.count}`;
                return new google.maps.marker.AdvancedMarkerElement({
                    position: cluster.position,
                    map: map1,
                    content: _node, // pass basic HTML element w/ custom content
                });
            }
        }
        return _renderer;
    }, []);

    /**
     * Memoize to ensure we don't render markers too often; else it overloads JS, slowing down site
     * @source
     * - https://developers.google.com/maps/documentation/javascript/advanced-markers/html-markers
     * - https://developers.google.com/maps/documentation/javascript/examples/advanced-markers-html
     */
    const listingMarkers = useMemo(() => {
        console.info(`memo.listingMarkers -> ${props.listings.length}`);
        return props.listings.map((lst, index) => {
            const mlsId = lst.NO_INSCRIPTION!;
            return <AdvancedMarker
                key={`marker-${mlsId}`} className={styles.price_marker}
                ref={marker => setMarkerRef(marker, mlsId)}
                position={{
                    lat: parseFloat(lst.LATITUDE!),
                    lng: parseFloat(lst.LONGITUDE!)
                } as google.maps.LatLngLiteral}
                onClick={e => setActiveMlsId(mlsId)}
            >
                {shortPrice(lst)}
                {/* note: custom pin is 10x slower to load 2000 markers */}
                {/*<Pin background={'#E60000'} glyphColor={'#242323'} borderColor={'#E60000'}/>*/}
            </AdvancedMarker>
        });
    }, [props.listings]);

    // memoized content of the InfoWindow when clicking any specific property's pin
    const infoWindowContent = useMemo(() => {
        console.info(`memo.infoWindowContent -> ${activeMlsId}`);
        const lst = findBy(props.listings, "NO_INSCRIPTION", activeMlsId);

        return <Card className={"rounded-none"}>
            {/* note: trailing slash is important to prevent double reloads (WP forces ending slash) */}
            <CardActionArea
                href={`${others.url_listing_single}${activeMlsId}/`}
                target={$config.listing_target_self ? "_self" : "_blank"}
            >
                {/* property thumbnail */}
                {/* note: line-height:0 is required to remove that weird blank space between img & div below */}
                <div className={"marker__thumbnail h-auto aspect-[3/2] hover:opacity-75 group relative leading-[0px]"}>
                    <img className={"absolute right-3 bottom-3 !z-10 w-10"}
                         src={$config.is_mcgill ? svgMgFavicon : svgEvFavicon}
                         alt={`Watermark`}
                    ></img>

                    {/* property's thumbnail image (fallback on solid-color image placeholder) */}
                    <img src={photoByMls(activeMlsId) ?? ""} alt={""}
                         className={"!h-full w-full object-cover"}
                         onContextMenu={event => event.preventDefault()}
                         onError={({currentTarget}) => {
                             currentTarget.onerror = null; // prevents looping
                             currentTarget.src = centrisPhoto(activeMlsId) ?? imgPlaceholder;
                         }}
                    ></img>

                    {/* SOLD status chip */}
                    {lst?.CODE_STATUT == "VE" && <Chip
                        color={"error"}
                        className={"rounded absolute left-4 top-4 !z-10 leading-normal"}
                        label={fixedValue(CtrDomainsVf.CodeStatut, lst?.CODE_STATUT)}
                    ></Chip>}
                </div>

                <div className={"px-2 py-1"}>
                    <div className={"text-lg font-medium flex flex-wrap items-baseline gap-x-1"}>
                        {price(lst)}
                        {lst?.CODE_STATUT == "VE" && (
                            <span className={"text-xs"}>{__("Prix demandé")}</span>
                        )}
                    </div>
                    <div>{title(lst)}</div>
                    <div>{shortAddress(lst)}</div>
                </div>
            </CardActionArea>
        </Card>
    }, [activeMlsId]);

    // listen for flag indicating that the map is being focused (used to close InfoWindow when clicking on map)
    useEffect(() => {
        if (props.mapActivityFlag == 0) return;
        setActiveMlsId(null);
    }, [props.mapActivityFlag]);

    // initialize marker clusterer loading of the map
    useEffect(() => {
        if (!map) return;
        if (!clusterer.current) {
            console.info(`set clusterer.current -> ${map.getMapTypeId()}`);
            clusterer.current = new MarkerClusterer({map, renderer: clusterRenderer});
        }
        centerMap();
    }, [map]);

    // update markers in clusterer
    useEffect(() => {
        console.info(`markers.useEffect`, Object.keys(markers).length);
        clusterer.current?.clearMarkers();
        clusterer.current?.addMarkers(Object.values(markers));
        centerMap();
    }, [markers]);

    // when listings change (due to filtering), remove all irrelevant markers so map reflects the filters
    useEffect(() => {
        console.info(`listings.useEffect`, props.listings.length);
        // https://www.30secondsofcode.org/js/s/filter-object-keys/#filter-object-properties-by-key
        const mlsIds = props.listings.map(it => it.NO_INSCRIPTION!);
        const pick = Object.entries(markers).filter(([key, marker]) => mlsIds.includes(key));
        setMarkers(Object.fromEntries(pick));
    }, [props.listings]);

    return <Fragment>
        {/* note: Marker is deprecated, use AdvancedMarker which requires using a Map ID */}
        {/* basic list of all markers (all properties) */}
        {listingMarkers}

        {/* popup when clicking on property marker */}
        {Boolean(activeMlsId) && <InfoWindow
            anchor={markers[activeMlsId!] ?? null}
            className={"iw__body"}
            maxWidth={300}
            minWidth={300}
            onCloseClick={() => setActiveMlsId(null)}
            headerDisabled={true}
        >
            {infoWindowContent}
        </InfoWindow>}
    </Fragment>
}

export default MapViewMarkers;
